import { PlayerEvent } from '@/organisms/AngelPlayer'
import { logger } from '@/utils/logging'

const smpteMatcher = /^[0-9]{2}:([0-5][0-9]):([0-5][0-9]):([0-1][0-9]|[2][0-3])$/

export const SmtpeController = /** @class */ (function () {
  function SmtpeController(player, assetDescription, bitmovin, autoplay) {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const _this = this
    /**
     * In Firefox we have the fun behaviour, that play + pause leads to an incorrect video.currentTime (off by 1 frame
     * most of the time). To reproduce try play + pause at some point in the video. Then save the current Time, seek
     * somewhere else and then back to the previous current time ... you will see a different Frame.
     * In order to avoid this mess, when we encounter play + pause we seek to the calculated SMPTE, quite the dirty hack
     * but necessary.
     */
    this.playPauseHandler = function (event) {
      if (event.type === 'playing') {
        _this.hasBeenPlaying = true
      } else if (_this.hasBeenPlaying) {
        _this.hasBeenPlaying = false
        _this.seekToSMPTE(_this.getCurrentSmpte())
      }
    }
    this.player = player
    this.assetDescription = assetDescription
    this.autoplay = autoplay
    player.on(PlayerEvent.Paused, this.playPauseHandler)
    player.on(PlayerEvent.Playing, this.playPauseHandler)
    this.load(assetDescription)
    this.destroyed = false
  }

  SmtpeController.prototype.isPlayerReady = function () {
    return this.player && !this.destroyed
  }

  /**
   * Calls load on the player with asset.sourceConfig and updates the internal asset description
   */
  SmtpeController.prototype.load = function (asset) {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const _this = this

    if (_this.loading) return

    _this.loading = true

    if (!this.player) {
      logger().warn('Player not initialized during loading of source media', { asset })
      return null
    }

    return this.player
      .load(asset.sourceConfig)
      .then(function () {
        _this.assetDescription = asset
        _this.hasBeenPlaying = false
      })
      .then(() => {
        if (asset?.position) {
          if (typeof asset.position === 'number') {
            _this.seek(asset.position)
          } else if (smpteMatcher.test(asset.position)) {
            _this.seekToSMPTE(asset.position)
          }
        }
        if (this.autoplay) {
          _this.play()
        }
      })
      .finally(() => {
        _this.loading = false
      })
      .catch(function (error) {
        const context = asset ? { asset } : { asset: 'undefined' }
        logger().error(`Bitmovin player load failed ${error.message || error.msg || error}`, context, error)
      })
  }
  /**
   * Converts the given SMTPE timestamp to a time and calls seek on the wrapped player to the calculated time
   * @param {string} smpteString a string in the form of HH:MM:SS:FF
   * @throws {string} an error if the given SMPTE was invalid
   */
  SmtpeController.prototype.seekToSMPTE = function (smpteString) {
    if (!this.isPlayerReady()) return

    const smpte = new SmpteTimestamp(smpteString, this.assetDescription)
    const targetTime = smpte.toAdjustedTime()
    this.player.seek(targetTime)
  }
  /**
   * Queries the time of the wrapped player and converts it to the SMPTE format
   */
  SmtpeController.prototype.getCurrentSmpte = function () {
    if (!this.isPlayerReady()) return

    const currentTime = this.player.getCurrentTime()
    return SmpteTimestamp.fromTimeWithAdjustments(currentTime, this.assetDescription).toString()
  }

  /**
   * Calls seek on the wrapped player to the calculated time in seconds
   */
  SmtpeController.prototype.seek = function (seekTo) {
    if (!this.isPlayerReady()) return

    this.player.seek(seekTo)
  }

  /**
   * Returns the current time in seconds from the player
   * @returns {number}
   */
  SmtpeController.prototype.getCurrentTime = function () {
    if (!this.isPlayerReady()) return

    return this.player.getCurrentTime()
  }

  /**
   * Returns if the player is currently playing
   */
  SmtpeController.prototype.isPlaying = function () {
    if (!this.isPlayerReady()) return

    return this.player.isPlaying()
  }

  /**
   * Plays the current player, catching the error if the browser blocks it
   */
  SmtpeController.prototype.play = function () {
    if (!this.isPlayerReady()) return

    this.player?.play?.().catch((e) => {
      if (e.name !== 'NotAllowedError' && e.name !== 'SOURCE_INVALID' && e.name !== 'API_NOT_AVAILABLE') {
        throw e
      }
    })
  }

  /**
   * Pauses the current player
   */
  SmtpeController.prototype.pause = function () {
    if (!this.isPlayerReady()) return

    this.player?.pause()
  }

  /**
   * Enters fullscreen for the current player
   */
  SmtpeController.prototype.enterFullscreen = function () {
    if (!this.isPlayerReady()) return

    this.player?.setViewMode('fullscreen')
  }

  /**
   * Exits fullscreen for the current player
   */
  SmtpeController.prototype.exitFullscreen = function () {
    if (!this.isPlayerReady()) return

    this.player?.setViewMode('inline')
  }

  /**
   * Advances `stepSize` frames in the current video
   * @param {number} stepSize number of frames to step, if negative will step to previous frames
   */
  SmtpeController.prototype.step = function (stepSize) {
    const smpte = new SmpteTimestamp(this.getCurrentSmpte(), this.assetDescription)
    if (smpte.minutes % 10 !== 0 && smpte.seconds === 0) {
      //  in the case of being around the dropped frame at step start we have to ignore the frameHoles
      smpte.addFrame(stepSize, false)
    } else {
      smpte.addFrame(stepSize, true)
    }
    this.seekToSMPTE(smpte.toString())
  }

  SmtpeController.prototype.destroy = function () {
    this.destroyed = true
    this.player.destroy()
  }

  SmtpeController.prototype.setAudio = function (audioId) {
    if (!this.isPlayerReady()) return
    this.player.setAudio(audioId)
  }

  SmtpeController.prototype.getSubtitleList = function () {
    if (!this.isPlayerReady() || !this.player.subtitles) return
    const subtitleList = this.player.subtitles.list()

    return subtitleList ? subtitleList : []
  }

  SmtpeController.prototype.enableSubTitle = function (subtitleTrackId) {
    if (!this.isPlayerReady()) return

    this.player.subtitles.enable(subtitleTrackId)
  }

  SmtpeController.prototype.getAvailableAudio = function () {
    if (!this.isPlayerReady()) return

    return this.player.getAvailableAudio()
  }

  return SmtpeController
})()

export const AssetDescription = /** @class */ (function () {
  function AssetDescription(props) {
    const { name, sourceConfig, frameRate, adjustmentFactor, framesDroppedEachMinute, position } = props

    this.name = name
    this.sourceConfig = sourceConfig
    this.framesPerSecond = frameRate
    this.adjustmentFactor = adjustmentFactor
    this.framesDroppedAtFullMinute = framesDroppedEachMinute
    if (adjustmentFactor == null) {
      this.adjustmentFactor = Math.ceil(frameRate) / frameRate
    }
    if (framesDroppedEachMinute == null) {
      if (frameRate === 29.97 || frameRate === 29.98) {
        this.framesDroppedAtFullMinute = 2
      } else {
        this.framesDroppedAtFullMinute = 0
      }
    }
    this.frameDuration = 1 / this.framesPerSecond
    // needed to not seek to the end of the previous frame but rather to the middle of the desired one
    this.offsetToMidFrame = this.frameDuration / 2 / this.adjustmentFactor
    this.position = position ?? 0
  }
  return AssetDescription
})()

const SmpteTimestamp = /** @class */ (function () {
  function SmpteTimestamp(smtpeTimestamp, assetDescription) {
    this.assetDescription = assetDescription
    if (smtpeTimestamp && isFinite(smtpeTimestamp)) {
      let smpteValue = smtpeTimestamp
      this.frame = smpteValue % 100
      smpteValue = Math.floor(smpteValue / 100)
      this.seconds = smpteValue % 100
      smpteValue = Math.floor(smpteValue / 100)
      this.minutes = smpteValue % 100
      this.hours = Math.floor(smpteValue / 100)
    } else if (smtpeTimestamp && SmpteTimestamp.validateTimeStamp(smtpeTimestamp, assetDescription.framesPerSecond)) {
      const parts = smtpeTimestamp.split(':')
      this.hours = Number(parts[0])
      this.minutes = Number(parts[1])
      this.seconds = Number(parts[2])
      this.frame = Number(parts[3])
    } else {
      this.hours = 0
      this.minutes = 0
      this.seconds = 0
      this.frame = 0
    }
  }
  SmpteTimestamp.validateTimeStamp = function (smtpeTimestamp, framesPerSecond) {
    // validate SMTPE timecode
    const isValidSMPTETimeCode = /(^(?:(?:[0-1][0-9]|[0-2][0-3]):)(?:[0-5][0-9]:){2}(?:[0-6][0-9])$)/
    if (!isValidSMPTETimeCode.test(smtpeTimestamp)) {
      // invalid format
      throw new Error(smtpeTimestamp + ' does not match a SMPTE timecode HH:MM:SS:FF')
    }
    if (Number(smtpeTimestamp.split(':')[3]) >= framesPerSecond) {
      // frame portion of the input is higher than the FPS
      throw new Error('Frame Number in SMPTE is higher than FPS: ' + smtpeTimestamp)
    }
    return true
  }
  SmpteTimestamp.padNum = function (num) {
    if (num < 10) {
      return '0' + num
    } else {
      return String(num)
    }
  }
  SmpteTimestamp.prototype.toString = function () {
    return (
      SmpteTimestamp.padNum(this.hours) +
      ':' +
      SmpteTimestamp.padNum(this.minutes) +
      ':' +
      SmpteTimestamp.padNum(this.seconds) +
      ':' +
      SmpteTimestamp.padNum(this.frame)
    )
  }
  SmpteTimestamp.prototype.toTime = function () {
    let timeInSeconds = this.hours * 3600 + this.minutes * 60 + this.seconds
    // convert frame number to time and add it
    timeInSeconds += this.frame * this.assetDescription.frameDuration
    return timeInSeconds
  }
  SmpteTimestamp.prototype.toAdjustedTime = function () {
    // take dropped frames around every full minute (except for every 10minutes) into account
    if (this.assetDescription.framesDroppedAtFullMinute > 0) {
      const totalMinutes = this.hours * 60 + this.minutes
      let framesToAdd = totalMinutes - Math.floor(totalMinutes / 10)
      framesToAdd *= this.assetDescription.framesDroppedAtFullMinute
      this.addFrame(-framesToAdd, false)
    }
    let targetTime = this.toTime() * this.assetDescription.adjustmentFactor
    targetTime += this.assetDescription.offsetToMidFrame
    targetTime = Math.floor(targetTime * 1000) / 1000
    return targetTime
  }
  SmpteTimestamp.fromString = function (smtpeTimestamp, assetDescription) {
    return new SmpteTimestamp(smtpeTimestamp, assetDescription)
  }
  SmpteTimestamp.fromTime = function (timestamp, assetDesc) {
    // to get to the start of the actual frame... use this
    let tmp = timestamp
    const retVal = new SmpteTimestamp(null, assetDesc)
    retVal.hours = Math.floor(tmp / 3600)
    tmp -= retVal.hours * 3600
    retVal.minutes = Math.floor(tmp / 60)
    tmp -= retVal.minutes * 60
    retVal.seconds = Math.floor(tmp)
    tmp -= retVal.seconds
    retVal.frame = Math.floor(tmp / assetDesc.frameDuration)
    return retVal
  }
  SmpteTimestamp.fromTimeWithAdjustments = function (timestamp, assetDesc) {
    const time = timestamp / assetDesc.adjustmentFactor
    const smtpe = SmpteTimestamp.fromTime(time, assetDesc)
    if (assetDesc.framesDroppedAtFullMinute > 0) {
      let numMinutesWithDroppedFrames = smtpe.minutes + smtpe.hours * 60
      // no frames dropped at every 10 minutes
      numMinutesWithDroppedFrames -= Math.floor(numMinutesWithDroppedFrames / 10)
      const framesToAdd = numMinutesWithDroppedFrames * assetDesc.framesDroppedAtFullMinute
      const minutesBefore = smtpe.minutes
      smtpe.addFrame(framesToAdd, false)
      if (smtpe.minutes % 10 !== 0 && minutesBefore !== smtpe.minutes) {
        smtpe.addFrame(assetDesc.framesDroppedAtFullMinute, false)
      }
    }
    return smtpe
  }
  SmpteTimestamp.prototype.addFrame = function (framesToAdd, fixFrameHoles) {
    // eslint-disable-next-line no-void
    if (fixFrameHoles === void 0) {
      fixFrameHoles = true
    }
    let _a
    this.frame += framesToAdd
    let overflow
      // eslint-disable-next-line no-unused-expressions,no-sequences
    ;(_a = SmpteTimestamp.fitIntoRange(this.frame, Math.ceil(this.assetDescription.framesPerSecond))),
      (this.frame = _a[0]),
      (overflow = _a[1])
    if (overflow !== 0) {
      this.addSeconds(overflow)
    }
    // make sure we dont step into a frame hole
    if (fixFrameHoles && this.assetDescription.framesDroppedAtFullMinute > 0 && this.minutes % 10 !== 0) {
      if (framesToAdd > 0 && this.seconds === 0) {
        this.addFrame(this.assetDescription.framesDroppedAtFullMinute, false)
      }
    }
  }
  SmpteTimestamp.prototype.addSeconds = function (secondsToAdd) {
    let _a
    this.seconds += secondsToAdd
    let overflow
      // eslint-disable-next-line no-unused-expressions,no-sequences
    ;(_a = SmpteTimestamp.fitIntoRange(this.seconds, 60)), (this.seconds = _a[0]), (overflow = _a[1])
    if (overflow !== 0) {
      this.addMinute(overflow)
    }
  }
  SmpteTimestamp.prototype.addMinute = function (minutesToAdd) {
    let _a
    this.minutes += minutesToAdd
    let overflow
      // eslint-disable-next-line no-unused-expressions,no-sequences
    ;(_a = SmpteTimestamp.fitIntoRange(this.minutes, 60)), (this.minutes = _a[0]), (overflow = _a[1])
    if (overflow !== 0) {
      this.addHour(overflow)
    }
  }
  SmpteTimestamp.prototype.addHour = function (hoursToAdd) {
    this.hours += hoursToAdd
    if (this.hours < 0) {
      this.hours = 0
      this.minutes = 0
      this.seconds = 0
      this.frame = 0
    }
  }
  SmpteTimestamp.fitIntoRange = function (toFit, range) {
    let overflow = 0
    if (toFit < 0) {
      while (toFit < 0) {
        overflow--
        toFit += range
      }
    } else if (toFit >= range) {
      while (toFit >= range) {
        overflow++
        toFit -= range
      }
    }
    return [toFit, overflow]
  }
  return SmpteTimestamp
})()
